<script setup lang="ts">
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import ColorPicker from "./components/ColorPicker/index.vue";

import { ref } from "vue";
import { BorderRadius, type Radius } from "./components/BorderRadius";

import ThemeSwitch from "./components/ThemeSwitch.vue";
import Button from "@/components/ui/button/Button.vue";
import type {
    PrimaryColorValue,
    GrayColor,
} from "./components/ColorPicker/colors";
import { router } from "@inertiajs/vue3";

const emit = defineEmits(["onSuccess"]);

const borderRadius = ref<Radius>("6px");
const darkMode = ref<boolean>(false);

const primaryColor = ref<PrimaryColorValue>("red");
const grayColor = ref<GrayColor>("slate");
const name = ref();

const opened = ref(false);

async function saveChanges() {
    router.post(
        "/appearances",
        {
            name: name.value || `${primaryColor.value}-${grayColor.value}`,
            data: {
                primaryColor: primaryColor.value,
                grayColor: grayColor.value,
                borderRadius: borderRadius.value,
                defaultSize: "mobile",
                darkMode: darkMode.value,
            },
        },
        {
            preserveState: true,
            onSuccess: () => {
                opened.value = false;
                emit("onSuccess");
            },
        },
    );
}
</script>

<template>
    <Dialog v-model:open="opened">
        <DialogTrigger> <slot /> </DialogTrigger>
        <DialogContent>
            <DialogHeader>
                <DialogTitle>{{ $t("appearances.create.title") }}</DialogTitle>
                <DialogDescription>
                    <p>
                        {{ $t("appearances.create.description") }}
                    </p>
                </DialogDescription>
            </DialogHeader>

            <div class="max-w-sm gap-1.5 grid w-full items-center">
                <Label for="name">{{ $t("appearances.create.name") }}</Label>
                <Input
                    id="name"
                    v-model="name"
                    type="text"
                    :placeholder="$t('appearances.create.name_placeholder')"
                />
            </div>

            <ColorPicker
                v-model:primary="primaryColor"
                v-model:gray="grayColor"
            />

            <ThemeSwitch v-model="darkMode" />
            <BorderRadius v-model="borderRadius" />

            <DialogFooter>
                <Button @click="saveChanges">{{
                    $t("appearances.create.create")
                }}</Button>
            </DialogFooter>
        </DialogContent>
    </Dialog>
</template>
